import { useEffect, useRef } from 'react';

let openModals = 0;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getTopModal = (): HTMLElement | null => {
  const modals = document.querySelectorAll<HTMLElement>(
    '[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]',
  );
  const visibleModals = Array.from(modals).filter((modal) => modal.getClientRects().length > 0);

  return visibleModals[visibleModals.length - 1] ?? null;
};

const getFocusableElements = (modal: HTMLElement): HTMLElement[] =>
  Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      !element.hidden &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.getClientRects().length > 0,
  );

const useModalBehavior = (onEscape?: () => void, isActive = true) => {
  const onEscapeRef = useRef(onEscape);

  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!isActive) return undefined;

    const previousActiveElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    let modal: HTMLElement | null = null;
    let addedTabIndex = false;

    const focusFrame = window.requestAnimationFrame(() => {
      modal = getTopModal();

      if (!modal) return;

      if (!modal.hasAttribute('tabindex')) {
        modal.setAttribute('tabindex', '-1');
        addedTabIndex = true;
      }

      (getFocusableElements(modal)[0] ?? modal).focus();
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (!modal || getTopModal() !== modal) return;

      if (event.key === 'Escape' && onEscapeRef.current) {
        event.preventDefault();
        onEscapeRef.current();

        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(modal);

      if (focusableElements.length === 0) {
        event.preventDefault();
        modal.focus();

        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        (document.activeElement === firstElement || !modal.contains(document.activeElement))
      ) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKeyDown);

      if (addedTabIndex) modal?.removeAttribute('tabindex');
      if (previousActiveElement?.isConnected) previousActiveElement.focus();
    };
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return undefined;

    openModals += 1;
    document.body.style.overflow = 'hidden';

    return () => {
      openModals -= 1;

      if (openModals === 0) document.body.style.overflow = '';
    };
  }, [isActive]);
};

export default useModalBehavior;
